import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

/**
 * A yellow ⚠️ marker for LEGACY data — records that carry no organization id and are therefore
 * shown inside the active organization's views so the user doesn't lose access to them. Tapping
 * it opens a modal that explains WHY the item is visible here.
 *
 * Optionally lets the user FIX it: pick which organization the item should belong to. When
 * `onAssign` and `organizations` are supplied, the modal shows an org picker + Assign button;
 * `onAssign(orgId)` performs the surface-specific write (the caller stamps the record and refreshes
 * its list). After a successful assign the item is org-scoped and stops appearing in other orgs.
 *
 * Use it anywhere a legacy (null-org) item is rendered, so the explanation is consistent across
 * the app: chat rooms, quiz results, reports, etc. Only render it when the item is actually legacy
 * (e.g. `!item.organizationID`); this component does not decide that.
 *
 * The icon click stops propagation, so placing it inside a clickable row (a chat room, a card)
 * won't also trigger that row's onClick.
 *
 * @param {object} props
 * @param {'generic'|'chat'|'quiz'|'learning'|'report'|'project'|'award'} [props.type='generic']
 * @param {string} [props.className]
 * @param {object} [props.style]
 * @param {Array<{id: string, name: string}>} [props.organizations] Orgs the user can assign to.
 * @param {string} [props.currentOrgId] The active org id (default selection / "(current)" hint).
 * @param {(orgId: string) => Promise<void>} [props.onAssign] Persist the chosen org; should also
 *        refresh the surface. Throwing surfaces an error in the modal.
 */

const MESSAGES = {
  generic: 'This item',
  chat: 'This chat',
  quiz: 'This quiz result',
  learning: 'This learning progress',
  report: 'This report',
  project: 'This project',
  award: 'This award',
};

const DETAILS = {
  generic: "It was created before it was linked to an organization, so it isn't tied to a specific workspace yet.",
  chat: 'It was created before chats were grouped by organization, so it isn’t linked to one yet.',
  quiz: 'It was recorded before quiz results were linked to an organization, so it isn’t tied to a specific workspace yet.',
  learning: 'It was recorded before learning activity was linked to an organization, so it isn’t tied to a specific workspace yet.',
  report: "It was created before reports were linked to an organization, so it isn't tied to a specific workspace yet.",
  project: "It was created before projects were linked to an organization, so it isn't tied to a specific workspace yet.",
  award: "It was earned before awards were linked to an organization, so it isn't tied to a specific workspace yet.",
};

const LegacyDataBadge = ({ type = 'generic', className = '', style, organizations, currentOrgId, onAssign }) => {
  const [show, setShow] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(currentOrgId || '');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const noun = MESSAGES[type] || MESSAGES.generic;
  const detail = DETAILS[type] || DETAILS.generic;
  const canAssign =
    typeof onAssign === 'function' && Array.isArray(organizations) && organizations.length > 0;

  // Reset the picker to the current org each time the modal opens.
  useEffect(() => {
    if (show) {
      setSelectedOrg(currentOrgId || (organizations && organizations[0]?.id) || '');
      setAssignError(null);
    }
  }, [show, currentOrgId, organizations]);

  const open = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShow(true);
  };
  const close = (e) => {
    if (e) e.stopPropagation();
    if (assigning) return; // don't close mid-write
    setShow(false);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') open(e);
  };

  const handleAssign = async () => {
    if (!selectedOrg || assigning) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await onAssign(selectedOrg);
      setShow(false);
    } catch (err) {
      console.error('Legacy assign failed:', err);
      setAssignError('Could not assign this to the organization. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-label="Why am I seeing this?"
        title="Why am I seeing this?"
        onClick={open}
        onKeyDown={onKeyDown}
        className={`text-warning ${className}`}
        style={{ cursor: 'pointer', display: 'inline-flex', ...style }}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} />
      </span>

      <Modal show={show} onHide={close} centered size="sm">
        <Modal.Header closeButton={!assigning}>
          <Modal.Title className="d-flex align-items-center" style={{ fontSize: '1.05rem' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
            Why am I seeing this?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            <strong>{noun}</strong> isn&rsquo;t linked to an organization yet.
          </p>
          <p className="mb-2 text-muted">{detail}</p>
          <p className="mb-0 text-muted">
            It stays visible so you don&rsquo;t lose access to it. Once it&rsquo;s used inside an
            organization, it will only appear there.
          </p>

          {canAssign && (
            <>
              <hr />
              <Form.Label className="fw-semibold mb-1">Keep this in:</Form.Label>
              <Form.Select
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
                disabled={assigning}
                size="sm"
              >
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}{o.id === currentOrgId ? ' (current)' : ''}
                  </option>
                ))}
              </Form.Select>
              <div className="form-text">
                It will belong to that organization and stop appearing in the others.
              </div>
              {assignError && <div className="text-danger small mt-2">{assignError}</div>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {canAssign && (
            <Button variant="primary" size="sm" onClick={handleAssign} disabled={assigning || !selectedOrg}>
              {assigning ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" /> Assigning…
                </>
              ) : (
                'Assign'
              )}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={close} disabled={assigning}>
            {canAssign ? 'Cancel' : 'Got it'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LegacyDataBadge;
